import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Modal, useToast } from "@/components/common";
import { costsService } from "@/api/services/costs.service";
import { quotesService } from "@/api/services/quotes.service";
import { formatCurrency } from "@/utils/formatters";
import { formatScopeOfWorksAsBullets, getScopeOfWorksItems, } from "@/utils/quoteHelpers";
import styles from "./CreateAdditionalWorksInvoiceModal.module.css";
const parseAmountFromDisplay = (value) => {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned);
};
/** Format for input with separate $ prefix - returns "1,234.50" without $ */
const formatAmountForInput = (amount) => formatCurrency(amount).replace(/^\$/, "");
const withJobSuffix = (desc, jobNumber) => jobNumber ? `${(desc || "").trim()} (Job# ${jobNumber})` : (desc ?? "");
function computeQuoteTotal(quote) {
    const subTotalWithMargin = (quote.subTotal || 0) +
        (quote.buildersMarginEnabled && quote.buildersMarginAmount
            ? quote.buildersMarginAmount
            : 0);
    return subTotalWithMargin * 1.1;
}
export const CreateAdditionalWorksInvoiceModal = ({ isOpen, onClose, onSave, quoteId, fallbackAmount, fallbackDescription, jobNumber, }) => {
    const { warning } = useToast();
    const [invoiceAmount, setInvoiceAmount] = useState("");
    const [description, setDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
    const [scopeItems, setScopeItems] = useState([]);
    const applyPrefill = useCallback((amount, desc, items = []) => {
        if (amount != null && !Number.isNaN(amount) && amount > 0) {
            setInvoiceAmount(formatAmountForInput(amount));
        }
        else {
            setInvoiceAmount("");
        }
        setDescription(desc ?? "");
        setScopeItems(items);
    }, []);
    useEffect(() => {
        if (!isOpen)
            return;
        if (quoteId) {
            let cancelled = false;
            setIsLoadingPrefill(true);
            Promise.all([quotesService.getQuote(quoteId), costsService.getGroups()])
                .then(([quoteRes, groupsRes]) => {
                if (cancelled)
                    return;
                const quote = quoteRes.data;
                const costGroups = groupsRes.data ?? [];
                const amount = computeQuoteTotal(quote);
                const items = getScopeOfWorksItems(quote.lineItems, costGroups);
                const bulletedScope = formatScopeOfWorksAsBullets(items);
                const desc = bulletedScope.length > 0
                    ? `The provision of defit services including:\n${bulletedScope}`
                    : (fallbackDescription ?? "");
                applyPrefill(amount, withJobSuffix(desc, jobNumber), items);
            })
                .catch(() => {
                if (!cancelled)
                    applyPrefill(fallbackAmount, withJobSuffix(fallbackDescription ?? "", jobNumber), []);
            })
                .finally(() => {
                if (!cancelled)
                    setIsLoadingPrefill(false);
            });
            return () => {
                cancelled = true;
            };
        }
        if (fallbackAmount != null &&
            !Number.isNaN(fallbackAmount) &&
            fallbackAmount >= 0) {
            setInvoiceAmount(formatAmountForInput(fallbackAmount));
        }
        else {
            setInvoiceAmount("");
        }
        setDescription(withJobSuffix(fallbackDescription ?? "", jobNumber));
        setScopeItems([]);
        setIsLoadingPrefill(false);
    }, [
        isOpen,
        quoteId,
        fallbackAmount,
        fallbackDescription,
        jobNumber,
        applyPrefill,
    ]);
    const handleClose = () => {
        setInvoiceAmount("");
        setDescription("");
        setScopeItems([]);
        onClose();
    };
    const handleAmountBlur = () => {
        const num = parseAmountFromDisplay(invoiceAmount);
        if (!Number.isNaN(num) && num >= 0) {
            setInvoiceAmount(formatAmountForInput(num));
        }
    };
    const handleSave = async () => {
        const amount = parseAmountFromDisplay(invoiceAmount);
        if (!invoiceAmount.trim() || Number.isNaN(amount) || amount <= 0) {
            warning("Please enter a valid invoice amount");
            return;
        }
        setIsSaving(true);
        try {
            await onSave({
                invoiceAmount: amount,
                description: description.trim(),
                scopeItems: scopeItems.length > 0 ? scopeItems : undefined,
            });
            handleClose();
        }
        finally {
            setIsSaving(false);
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: styles.modalWrapper, children: [_jsx("div", { className: styles.modalHeader, children: _jsx("h2", { className: styles.modalTitle, children: "CREATE ADDITIONAL WORKS INVOICE" }) }), _jsx("div", { className: styles.modalBody, children: _jsxs("div", { className: styles.formStacked, children: [_jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.fieldLabel, children: "Invoice Amount" }), _jsx("div", { className: styles.inputWrapper, children: _jsxs("div", { className: styles.amountInput, children: [_jsx("span", { className: styles.currency, children: "$" }), _jsx("input", { type: "text", className: styles.amountField, value: invoiceAmount, onChange: (e) => setInvoiceAmount(e.target.value), onBlur: handleAmountBlur, placeholder: "0.00", disabled: isLoadingPrefill })] }) })] }), _jsxs("div", { className: styles.formField, children: [_jsx("label", { className: styles.fieldLabel, children: "Description" }), _jsx("div", { className: styles.inputWrapper, children: _jsx("textarea", { className: styles.textarea, value: description, onChange: (e) => setDescription(e.target.value), placeholder: "The provision of defit services including...", rows: 4, disabled: isLoadingPrefill }) })] })] }) }), _jsxs("div", { className: styles.modalFooter, children: [_jsx("button", { type: "button", className: styles.cancelBtn, onClick: handleClose, children: "Cancel" }), _jsx("button", { type: "button", className: styles.createBtn, onClick: handleSave, disabled: isSaving || isLoadingPrefill, children: isLoadingPrefill
                                ? "Loading..."
                                : isSaving
                                    ? "Creating..."
                                    : "Create" })] })] }) }));
};
